import React from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { Role } from 'store/user/auth0.helpers';
import { useUser } from 'store/user/useUser';
import { FeatureFlag, featureFlagIsTrue } from 'utils/helpers/featureFlag.helpers';
import { userHasPermissions } from 'utils/helpers/permissions.helpers';

type SecureRouteProps = {
  component: React.FC;
  roles?: Role[];
  featureFlag?: FeatureFlag;
  [key: string]: any;
};

/**
 * 1. Uses the withAuthenticationRequired HOC provided by @auth0/auth0-react to ensure that the user is authenticated.
 * 2. Checks that the user has the required roles to access this route (authorization).
 * 3. Checks that this feature is enabled for the user (feature flagging).
 * IF criteria are not met, user is redirected to 404
 */
const SecureRoute: React.FC<SecureRouteProps> = ({ component: Component, roles = [], featureFlag, ...rest }) => {
  const user = useUser();

  const userHasAllowedRole = userHasPermissions(user, roles);

  const canAccess = featureFlagIsTrue(featureFlag, user) && userHasAllowedRole;

  if (user.isLoading) {
    return <PageSpinner />;
  }

  return canAccess ? <Component {...rest} /> : <Navigate to="/404" />;
};

export default withAuthenticationRequired(SecureRoute, {
  onRedirecting: () => <PageSpinner />,
});
